/**
 * Diagnostic Decision review-document structure (RD_DD).
 *
 * Pure builder used by the React page and by unit tests so the reviewable
 * paragraph catalogue cannot drift between production UI and test helpers.
 *
 * Business rule — Final Formulation:
 * The clinician authors free-text notes on the multi-step DD "Final Formulation"
 * page (3_Formulation.js). Those notes are LLM-polished into
 * `dd_finalFormulation`, which the Patient Report / DD CAR read as an
 * approved-only placeholder (`{{dd_finalFormulation}}` under "How we reached
 * this conclusion").
 *
 * That page — and therefore this reviewable paragraph — exists only when the
 * workflow uses multi-step DD (`hasMultiStepDD`). Today that is TOC, SLaM /
 * 3 Borough, and WLC V2. Do NOT gate on:
 *   - `isTOCWorkflow` alone — that wrongly hides the paragraph for SLaM
 *     (the bug this helper exists to prevent);
 *   - `isFlatDifferentials` alone — flat DF can exist without multi-step DD
 *     (e.g. a future WLC flat-DF plan), and those orgs have no Formulation page;
 *   - `usesConditionalCarPipeline` — that flag is about CAR {{IF:}} rendering,
 *     not about whether the Formulation page / paragraph exists.
 *
 * Empty outputs are left in the structure; RD_DD and computeValidCodes filter
 * them out at render / approval time, so a missing generation never blocks
 * section completion.
 */

/**
 * Build the RD_DD docStructure map.
 *
 * @param {Object} params
 * @param {string[]} [params.conditionsToDecide=[]]  Non-ADHD conditions to
 *   decide, already sorted to match backend secondaryCondition_N ordering.
 * @param {Object} [params.reportDataOutputs={}]  manageReports.sections.3.outputs
 *   — used only to resolve human-readable condition titles.
 * @param {boolean} [params.isFlatDifferentials=false]  Flat DF uses different
 *   section titles; paragraph codes stay the same.
 * @param {boolean} [params.hasMultiStepDD=false]  When true, include the
 *   Final Formulation paragraph produced by the Formulation page.
 * @param {string[]} [params.secondaryComorbidDiagnoses]  Legacy alias for
 *   conditionsToDecide (kept so older tests keep working).
 * @returns {Object} Section title -> { key -> { title, code } }
 */
export function buildDDReviewDocStructure({
  conditionsToDecide = [],
  reportDataOutputs = {},
  isFlatDifferentials = false,
  hasMultiStepDD = false,
  // Legacy alias kept so existing reviewDocumentStructures tests don't break
  secondaryComorbidDiagnoses,
} = {}) {
  const conditions = secondaryComorbidDiagnoses ?? conditionsToDecide;

  const primarySectionTitle = isFlatDifferentials
    ? 'Decision for ADHD'
    : 'Final Diagnostic Decision';

  const baseStructure = {
    [primarySectionTitle]: {
      1: {
        title: 'Decision for Primary Diagnosis (Clinical Assessment Report)',
        code: 'dd_primaryDiagnosisSentence',
      },
      2: {
        title: 'Decision for Primary Diagnosis - Not ADHD (Patient Assessment Report)',
        code: 'pl_notADHD_ppdSentence',
      },
      3: {
        title: 'Your Next Steps',
        code: 'pl_yourNextStepsSentence',
      },
    },
  };

  if (conditions.length > 0) {
    const otherSectionTitle = isFlatDifferentials
      ? 'Decision for Other Conditions'
      : 'Secondary and/or Comorbid Diagnoses';

    baseStructure[otherSectionTitle] = {};

    for (let i = 0; i < conditions.length; i++) {
      const conditionNumber = i + 1;
      const conditionKey = `secondaryCondition_${conditionNumber}`;
      const conditionName = reportDataOutputs[conditionKey] || `[${conditionKey}]`;

      baseStructure[otherSectionTitle][i.toString()] = {
        title: `Decision for ${conditionName}`,
        code: `dd_secondaryCondition_${conditionNumber}_sentence`,
      };
    }
  }

  // Multi-step DD (TOC, SLaM / 3 Borough, WLC V2): Final Formulation notes
  // are LLM-polished into dd_finalFormulation and must be reviewable here so
  // the approved-only Patient Report / CAR placeholder can populate.
  if (hasMultiStepDD) {
    baseStructure['Final Formulation'] = {
      0: { title: 'Final Formulation', code: 'dd_finalFormulation' },
    };
  }

  return baseStructure;
}
